import { create } from 'zustand';
import { createSelectedChatSlice } from './selectedChat.slice';
import { TChatsStore } from './type';
import { chatApi } from '@/API';
import { TChatDataCamel, TCreateChatRequestCamel } from '@/types/chats';
import { convertKeys } from '@/utils/common';
import { loadInChunks } from '@/utils/loadInChunks';

// Chats are fetched in fixed-size pages.
const CHATS_PER_PAGE = 150;

export const useChatsStore = create<TChatsStore>((set, get, ...arg) => ({
  ...createSelectedChatSlice(set, get, ...arg),

  chats: [],
  loading: false,
  isAllChatsLoaded: false,
  getChat: async (chatId: string) => {
    set({ loadingSelectedChat: true });
    try {
      const response = await chatApi.getChat(chatId);
      return convertKeys(response.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      set({ loadingSelectedChat: false });
    }
  },
  getChats: async (institutionId?: string) => {
    // Load in sequential chunks: `loading` gates only the first page — once it
    // lands the UI is unblocked and the rest stream in the background — while
    // `isAllChatsLoaded` stays false until every page has been appended.
    set({ loading: true, isAllChatsLoaded: false, chats: [] });
    try {
      await loadInChunks(
        (page, perPage) =>
          chatApi.getChats({
            page,
            per_page: perPage,
            institution_id: institutionId,
          }),
        {
          onFirstPage: (items) =>
            set({ chats: convertKeys(items), loading: false }),
          onNextChunk: (items) => {
            const chunk: TChatDataCamel[] = convertKeys(items);
            set((state) => ({ chats: [...state.chats, ...chunk] }));
          },
        },
        CHATS_PER_PAGE,
      );

      set({ isAllChatsLoaded: true });
    } catch (error) {
      console.error('Failed to fetch chats:', error);
      set({ loading: false });
      throw error;
    }
  },
  joinChatByInvite: async (chatId: string, userInstitutionRoleId: string) => {
    await chatApi.putChatParticipants(
      chatId,
      convertKeys({ userInstitutionRoleId }, 'snake'),
    );
  },
  findChatById: (chatId: string) => get().chats.find(({ id }) => id === chatId),
  createChat: async (body: TCreateChatRequestCamel) => {
    const response = await chatApi.createChat(convertKeys(body, 'snake'));
    return convertKeys(response.data.data);
  },
  markMessagesAsRead: async (chatId: string, messageIds: string[]) => {
    await chatApi.putMarkMessagesAsRead(chatId, { message_ids: messageIds });
  },
}));
